import React, { useMemo, useState, useEffect } from "react";
import { FiLogOut, FiBell, FiBellOff } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { RxDashboard } from "react-icons/rx";
import toast from "react-hot-toast";
import CustomButton from "./CustomButton";
import {
  notificationsSupported,
  notifyPermissionState,
  requestNotifyPermission,
} from "../utils/browserNotify";

const Navbar = () => {
  const navigate = useNavigate();
  const [notifyPerm, setNotifyPerm] = useState(() => notifyPermissionState());

  const title = useMemo(() => {
    const t = localStorage.getItem("userType");
    if (!t || typeof t !== "string") return "Portal";
    return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
  }, []);

  const role = useMemo(
    () => String(localStorage.getItem("userType") || "").toLowerCase(),
    []
  );
  const showNotifyCta = ["student", "faculty", "admin"].includes(role);

  useEffect(() => {
    const sync = () => setNotifyPerm(notifyPermissionState());
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => document.removeEventListener("visibilitychange", sync);
  }, []);

  const logouthandler = () => {
    localStorage.removeItem("userToken");
    localStorage.removeItem("userType");
    navigate("/");
  };

  const onNotifyClick = async () => {
    if (!notificationsSupported()) {
      toast.error("This browser does not support notifications.");
      return;
    }
    if (notifyPerm === "denied") {
      toast.error("Notifications are blocked — enable them in browser site settings.");
      return;
    }
    if (notifyPerm === "granted") {
      try {
        new Notification("Devbhumi portal", {
          body: "Alerts are on. You’ll get messages & notices when this tab is in the background.",
          tag: "portal-test",
          icon: `${window.location.origin}/devbhumi-logo-icon.png`,
        });
      } catch {
        /* noop */
      }
      toast.success("Notifications are already enabled.");
      return;
    }
    const r = await requestNotifyPermission();
    setNotifyPerm(r);
    if (r === "granted") {
      toast.success("Done — you’ll get alerts for calls, chat & new notices (when tab is not active).");
    } else {
      toast.error("Permission not granted — try again from the lock icon in the address bar.");
    }
  };

  return (
    <header className="sticky top-0 z-50 shadow-sm border-b border-slate-200/80 dark:border-slate-700/80 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md w-full max-w-[100vw] min-w-0">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 w-full min-w-0">
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center min-w-0">
          <button
            type="button"
            className="font-semibold text-base sm:text-xl flex items-center gap-2 min-w-0 text-left text-slate-900 dark:text-slate-100"
            onClick={() => navigate("/")}
          >
            <RxDashboard className="shrink-0 text-indigo-600 dark:text-indigo-400 text-lg sm:text-xl" />
            <span className="truncate min-w-0">
              {title}{" "}
              <span className="text-slate-500 dark:text-slate-400 font-medium hidden sm:inline">
                Dashboard
              </span>
            </span>
          </button>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-3 w-full sm:w-auto min-w-0">
            {showNotifyCta && notificationsSupported() && notifyPerm !== "unsupported" ? (
              <CustomButton
                type="button"
                variant="primary"
                onClick={onNotifyClick}
                className="shrink-0 w-full sm:w-auto justify-center !py-2.5 !bg-indigo-600 hover:!bg-indigo-700 border-0"
                title={
                  notifyPerm === "granted"
                    ? "Notifications enabled"
                    : "Enable desktop / phone notifications"
                }
              >
                {notifyPerm === "granted" ? (
                  <FiBell className="mr-2 inline" />
                ) : (
                  <FiBellOff className="mr-2 inline opacity-80" />
                )}
                <span className="hidden sm:inline">
                  {notifyPerm === "granted" ? "Alerts on" : "Enable alerts"}
                </span>
                <span className="sm:hidden">
                  {notifyPerm === "granted" ? "Alerts on" : "Alerts"}
                </span>
              </CustomButton>
            ) : null}
            <CustomButton
              variant="danger"
              onClick={logouthandler}
              className="shrink-0 w-full sm:w-auto justify-center !py-2.5"
            >
              <FiLogOut className="mr-2 inline sm:mr-0 sm:ml-2" />
              Logout
            </CustomButton>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
